import * as React from 'react';
import { Control, Errors, LocalForm } from 'react-redux-form';

import { parseCurrency } from '@common/parsers';
import { getDefaultErrorMessages, hasValue } from '@common/validators';
import { ConfirmationRequestViewModel } from '@services/AutoAuthoService';
import { SpinnerPage } from '@common/components';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface AutoAuthoAuthorizationProperties {
  readonly authorize: (phoneNumber: string, swoId: number, request: ConfirmationRequestViewModel) => void;
  readonly cancel: () => void;
  readonly phoneNumber: string;
  readonly swoId: number;
  readonly tripStatusDescription: string;
}

interface AutoAuthoAuthorizationState {
  readonly hasNonCoveredCosts?: boolean;
  readonly isPossibleDenial?: boolean;
  readonly needsEquipmentOrdered?: boolean;
  readonly isSaving: boolean;
}

// Component definition
export class AutoAuthoAuthorization extends React.Component<
  AutoAuthoAuthorizationProperties,
  AutoAuthoAuthorizationState
> {
  constructor(props: AutoAuthoAuthorizationProperties) {
    super(props);
    this.state = {
      isSaving: false
    };
  }

  public render(): JSX.Element {
    const submitButtonProps =
      this.state.hasNonCoveredCosts == null ||
      this.state.isPossibleDenial == null ||
      this.state.needsEquipmentOrdered == null
        ? { disabled: true }
        : {};

    const clickedButtonClasses = 'btn btn-toggle-on';
    const defaultButtonClasses = 'btn btn-toggle-off';

    return (
      <div className="row center-xs">
        <LocalForm
          hideNativeErrors={true}
          onSubmit={m => {
            this.setState({
              isSaving: true
            });
            return this.props.authorize(this.props.phoneNumber, this.props.swoId, {
              authorizationAmount: m.authorizationAmount,
              hasNonCoveredCosts: this.state.hasNonCoveredCosts!,
              isPossibleDenial: this.state.isPossibleDenial!,
              needsEquipmentOrdered: this.state.needsEquipmentOrdered!
            });
          }}
        >
          {!this.state.isSaving ? (
            <div className="col">
              <h2>Review Eligibility</h2>
              <h4>SWO #: {this.props.swoId}</h4>
              <h4>SWO Trip Status: {this.props.tripStatusDescription}</h4>

              <div className="form-control">
                <label>1. Enter your requested authorization amount</label>
                <Control.text
                  min="0.00"
                  model=".authorizationAmount"
                  step="0.01"
                  parser={parseCurrency}
                  type="number"
                  updateOn={['blur', 'change']}
                  validators={{ required: hasValue }}
                />
                <Errors
                  className="error"
                  messages={getDefaultErrorMessages()}
                  model=".authorizationAmount"
                  show={{ touched: true, focus: false }}
                />
              </div>

              <div className="form-control">
                <label>2. Are there any non-covered costs?</label>
                <div className="row center-xs">
                  <button
                    className={this.state.hasNonCoveredCosts === true ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { hasNonCoveredCosts: true };
                      });
                    }}
                  >
                    Yes
                  </button>
                  <div className="col-xs-1 hidden-xs" />
                  <button
                    className={this.state.hasNonCoveredCosts === false ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { hasNonCoveredCosts: false };
                      });
                    }}
                  >
                    No
                  </button>
                </div>
                <div className="row center-xs">
                  <Errors
                    className="error"
                    messages={getDefaultErrorMessages()}
                    model=".hasNonCoveredCosts"
                    show={{ touched: true, focus: false }}
                  />
                </div>
              </div>

              <div className="form-control">
                <label>
                  3. Is this a possible denial? (e.g. denial in full or part, no failure to unit, secondary damage, or
                  unit in need of maintenance)
                </label>
                <div className="row center-xs">
                  <button
                    className={this.state.isPossibleDenial === true ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { isPossibleDenial: true };
                      });
                    }}
                  >
                    Yes
                  </button>
                  <div className="col-xs-1 hidden-xs" />
                  <button
                    className={this.state.isPossibleDenial === false ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { isPossibleDenial: false };
                      });
                    }}
                  >
                    No
                  </button>
                </div>
                <div className="row center-xs">
                  <Errors
                    className="error"
                    messages={getDefaultErrorMessages()}
                    model=".isPossibleDenial"
                    show={{ touched: true, focus: false }}
                  />
                </div>
              </div>

              <div className="form-control">
                <label>4. Does FNHW need to order any parts or equipment?</label>
                <div className="row center-xs">
                  <button
                    className={this.state.needsEquipmentOrdered === true ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { needsEquipmentOrdered: true };
                      });
                    }}
                  >
                    Yes
                  </button>
                  <div className="col-xs-1 hidden-xs" />
                  <button
                    className={this.state.needsEquipmentOrdered === false ? clickedButtonClasses : defaultButtonClasses}
                    onClick={e => {
                      e.preventDefault();
                      this.setState((prevState: AutoAuthoAuthorizationState) => {
                        return { needsEquipmentOrdered: false };
                      });
                    }}
                  >
                    No
                  </button>
                </div>
                <div className="row center-xs">
                  <Errors
                    className="error"
                    messages={getDefaultErrorMessages()}
                    model=".isPossibleDenial"
                    show={{ touched: true, focus: false }}
                  />
                </div>
              </div>
              <button {...submitButtonProps} className="btn btn-primary" type="submit">
                Submit
              </button>
              <button className="btn btn-secondary" onClick={() => this.props.cancel()} type="reset">
                Cancel
              </button>
            </div>
          ) : (
            <SpinnerPage />
          )}
        </LocalForm>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/auto-autho/_components/authorization.component.tsx